import type { GroupBase, MultiValue, Option, OptionsOrGroups } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Button,
    Card,
    Checkbox,
    Form,
    FormButtonRow,
    FormGroup,
    NumberInput,
    PageHeader,
    Radio,
    RadioGroup,
    Select,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPayoutBatchInformation } from '@common/utils/payoutBatchInformation';
import type { ISelectOption } from '@frontend/components/fields';
import type { IService } from '@frontend/components/interfaces/service';
import type { IFetchResult } from '@frontend/store/fetch';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, PayoutType } from '@common/enums';
import { BatchType } from '@common/enums/batchType';
import { formatString } from '@common/utils/formatString';
import { never } from '@common/utils/never';
import { paymentExportSchema } from '@common/validation/paymentExport';
import { BatchesTable } from '@frontend/components/elements/BatchesTable';
import { DateInput } from '@frontend/components/elements/common';
import { ExactAuthentication } from '@frontend/components/elements/ExactAuthentication';
import { ExactClientEventProgress } from '@frontend/components/elements/ExactClientEventProgress';
import {
    DownloadProgressFeedbackType,
    ExportDownloadProgress,
} from '@frontend/components/elements/ExportDownloadProgress';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { EnumSelect } from '@frontend/components/fields/EnumSelect';
import { SelectPeriod } from '@frontend/components/pages/processes/common';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';
import { useArrayUtils } from '@frontend/utils/hooks';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { PayoutRecordProviderGroupSelection } from './PayoutRecordProviderGroupSelection';
import { PayoutRecordProviderOrganizationSelection } from './PayoutRecordProviderOrganizationSelection';
import { PayoutRecordServiceProviderSelection } from './PayoutRecordServiceProviderSelection';

interface IServiceOption {
    group: string;
    text: string;
    value: string;
}

interface ISearchParams {
    activityTypes: PayoutType[];
    batchId: string | null;
    bookYear: number | null;
    endDate: string | null;
    episodeCategoryFilterValues: ISelectOption<string>[];
    episodeCategoryId: string | null;
    expectedPayoutDate: string | null;
    isPreview: boolean;
    providerGroup: string | null;
    providerOrganization: Option | null;
    sentBeforeDate: string | null;
    serviceChoice: 'specific' | 'all';
    serviceId: string | null;
    serviceProvider: Option | null;
    startDate: string | null;
}

export function PaymentsExport() {
    const [searchParams, setSearchParams] = useState<ISearchParams>({
        activityTypes: [],
        batchId: null,
        bookYear: null,
        endDate: null,
        episodeCategoryFilterValues: [],
        episodeCategoryId: null,
        expectedPayoutDate: null,
        isPreview: false,
        providerGroup: null,
        providerOrganization: null,
        sentBeforeDate: null,
        serviceChoice: 'all',
        serviceId: null,
        serviceProvider: null,
        startDate: null,
    });

    type ServiceOptionsGroup = GroupBase<ISelectOption<string>>;

    type ServiceOptions = OptionsOrGroups<ISelectOption<string>, ServiceOptionsGroup>;

    const { frontendSettings } = useFrontendSettings();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [serviceOptions, setServiceOptions] = useState<ServiceOptions>();
    const [selectedServiceOption, setSelectedServiceOption] = useState<ISelectOption<string> | null>(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [exactAllowsNormalExport, setExactAllowsNormalExport] = useState<boolean>(false);
    const [count, setCount] = useState<number>(0);
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [exportDownloadId, setExportDownloadId] = useState<string | undefined>();
    const [exactClientEventId, setExactClientEventId] = useState<string | undefined>();
    const [exportDownloadFinishedWithErrors, setExportDownloadFinishedWithErrors] = useState<boolean>(false);

    const translate = getSelectedLanguage();

    const abortableFetch = useAbortableFetch();
    const { arrayToCommaSeparatedString } = useArrayUtils();

    assertHasAuthorization(ManagerAuthorization.exportPayoutRecords);

    useEffect(() => {
        loadActivityTypeFilterAndEpisodeCategoryFilter();
    }, []);

    useEffect(() => {
        loadServices();
    }, [searchParams.episodeCategoryId]);

    useEffect(() => {
        setSelectedServiceOption(null);

        validateOne('serviceChoice', searchParams.serviceChoice);

        if (!(searchParams.serviceChoice === 'specific' && searchParams.serviceId === null)) {
            validateOne('serviceId', searchParams.serviceId);
        }

        if (searchParams.serviceChoice === 'all') {
            handleChange('serviceId', null);
        }
    }, [searchParams.serviceChoice]);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translate.finance.payments} />
            {frontendSettings.exactIntegrationEnabled && (
                <ExactAuthentication updateExactAllowsAction={setExactAllowsNormalExport} />
            )}

            <Form submitOnEnter={true}>
                <Card>
                    <Card.Header title={translate.finance.exportPaymentsActivities} />
                    <Card.Body>
                        <FormGroup
                            hasErrors={errors.has('sentBeforeDate')}
                            isRequired
                            label={translate.common.sentBeforeOrOn}
                        >
                            <DateInput
                                error={errors.get('sentBeforeDate')}
                                hasMaxWidth
                                onChange={(value) => handleChangeSentBeforeDate(value)}
                                onDoValidate={(value) => validateOne('sentBeforeDate', value)}
                                placeholder={translate.common.dateFormat}
                                size="large"
                                value={searchParams.sentBeforeDate}
                                variant="outline"
                            />
                        </FormGroup>

                        <PayoutRecordProviderGroupSelection
                            error={getValidationErrorTranslation(errors.get('providerGroup'))}
                            onChange={(value) => handleChange('providerGroup', value)}
                            onDoValidate={(value) => validateOne('providerGroup', value)}
                        />

                        <PayoutRecordProviderOrganizationSelection
                            error={getValidationErrorTranslation(errors.get('providerOrganization'))}
                            onChange={(value) => handleChange('providerOrganization', value)}
                            onDoValidate={(value) => validateOne('providerOrganization', value)}
                        />

                        <PayoutRecordServiceProviderSelection
                            error={getValidationErrorTranslation(errors.get('serviceProvider'))}
                            onChange={(value) => handleChange('serviceProvider', value)}
                            onDoValidate={(value) => validateOne('serviceProvider', value)}
                        />

                        <FormGroup
                            hasErrors={errors.has('episodeCategoryId')}
                            isRequired
                            label={translate.episodeCategory.episodeCategory}
                        >
                            <Select
                                error={getValidationErrorTranslation(errors.get('episodeCategoryId'))}
                                getOptionLabel={({ text }) => text}
                                getOptionValue={({ value }) => value}
                                onChange={(option) => {
                                    const newValue = option?.value || null;

                                    setSearchParams({ ...searchParams, episodeCategoryId: newValue, serviceId: null });
                                    setSelectedServiceOption(null);

                                    validateOne('episodeCategoryId', newValue);
                                }}
                                options={searchParams.episodeCategoryFilterValues}
                                placeholder={translate.common.selectOne}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('serviceId')}
                            label={translate.service.service}
                        >
                            <RadioGroup
                                layout="row"
                                name="choose service"
                                onChange={(e) => {
                                    setSearchParams((prev) => ({
                                        ...prev,
                                        serviceChoice: e.target.value as ISearchParams['serviceChoice'],
                                    }));
                                }}
                                value={searchParams.serviceChoice}
                            >
                                <Radio
                                    label={formatString(translate.common.allItems, [translate.service.services])}
                                    value="all"
                                />
                                <Radio
                                    label={formatString(translate.common.specificItem, [translate.service.service])}
                                    value="specific"
                                />
                            </RadioGroup>
                        </FormGroup>

                        {searchParams.serviceChoice === 'specific' && (
                            <FormGroup>
                                <Select
                                    error={getValidationErrorTranslation(errors.get('serviceId'))}
                                    getOptionLabel={({ text }) => text}
                                    getOptionValue={({ value }) => value}
                                    onChange={(option) => {
                                        const newValue = option?.value || null;

                                        setSelectedServiceOption(option);

                                        validateOne('serviceId', newValue);
                                        handleChange('serviceId', newValue);
                                    }}
                                    options={serviceOptions}
                                    placeholder={translate.common.selectOne}
                                    value={selectedServiceOption}
                                />
                            </FormGroup>
                        )}

                        <FormGroup label={translate.common.activityType}>
                            <EnumSelect
                                enumObj={PayoutType}
                                isClearable={true}
                                isMulti={true}
                                labels={translate.payoutType}
                                onChange={handleChangeActivityTypes}
                                placeholder={translate.common.selectOne}
                                value={searchParams.activityTypes}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('expectedPayoutDate')}
                            isRequired
                            label={translate.finance.expectedPayoutDate}
                        >
                            <DateInput
                                error={errors.get('expectedPayoutDate')}
                                hasMaxWidth
                                onChange={(value) => handleChange('expectedPayoutDate', value)}
                                onDoValidate={(value) => validateOne('expectedPayoutDate', value)}
                                placeholder={translate.common.dateFormat}
                                size="large"
                                value={searchParams.expectedPayoutDate}
                                variant="outline"
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('bookYear')}
                            isRequired
                            label={translate.finance.bookYear}
                        >
                            <NumberInput
                                error={getValidationErrorTranslation(errors.get('bookYear'))}
                                label={translate.common.yearFormat}
                                maxValue={9999}
                                onChange={(value) => {
                                    handleChange('bookYear', value);
                                    validateOne('bookYear', value);
                                }}
                                sx={{
                                    width: '155px',
                                }}
                                value={searchParams.bookYear}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('startDate') || errors.has('endDate')}
                            isRequired
                            label={translate.finance.period}
                            layout="horizontal"
                        >
                            <SelectPeriod
                                endDate={
                                    <DateInput
                                        error={errors.get('endDate')}
                                        hasMaxWidth
                                        onChange={(value) => handleChange('endDate', value)}
                                        onDoValidate={(value) => validateOne('endDate', value)}
                                        placeholder={translate.common.dateFormat}
                                        size="large"
                                        value={searchParams.endDate}
                                        variant="outline"
                                    />
                                }
                                endDateLabel={translate.common.until}
                                startDate={
                                    <DateInput
                                        error={errors.get('startDate')}
                                        hasMaxWidth
                                        onChange={(value) => handleChange('startDate', value)}
                                        onDoValidate={(value) => validateOne('startDate', value)}
                                        placeholder={translate.common.dateFormat}
                                        size="large"
                                        value={searchParams.startDate}
                                        variant="outline"
                                    />
                                }
                            />
                        </FormGroup>

                        <FormButtonRow>
                            <Checkbox
                                checked={searchParams.isPreview}
                                label="Proefexport"
                                name="isPreview"
                                onChange={(e) => handleChange('isPreview', e.target.checked)}
                            />
                            <Button
                                isDisabled={
                                    formDisabled ||
                                    (frontendSettings.exactIntegrationEnabled &&
                                        !exactAllowsNormalExport &&
                                        !searchParams.isPreview)
                                }
                                onClick={async () => await downloadExport()}
                            >
                                {translate.export.export}
                            </Button>
                        </FormButtonRow>

                        <ExportDownloadProgress
                            downloadProcessFeedbackType={DownloadProgressFeedbackType.progressPercentage}
                            exportDownloadId={exportDownloadId}
                            onFinished={async (error?: boolean) => {
                                setFormDisabled(false);
                                setExportDownloadFinishedWithErrors(!!error);
                            }}
                        />

                        {frontendSettings.exactIntegrationEnabled &&
                            exactClientEventId &&
                            !searchParams.isPreview &&
                            !exportDownloadFinishedWithErrors && (
                                <ExactClientEventProgress exactClientEventId={exactClientEventId} />
                            )}
                    </Card.Body>
                </Card>

                <Card>
                    <BatchesTable<IPayoutBatchInformation>
                        allowRerun={exactAllowsNormalExport}
                        batchType={BatchType.payout}
                        informationColumns={[
                            {
                                key: 'sentBeforeDate',
                                label: translate.common.sentBeforeOrOn,
                            },
                            {
                                key: 'episodeCategory',
                                label: translate.episodeCategory.episodeCategory,
                                translateKeyPrefix: 'episodeCategoryEnum',
                            },
                            {
                                key: 'serviceName',
                                label: translate.service.service,
                            },
                            {
                                key: 'providerGroup',
                                label: translate.groups.group,
                            },
                            {
                                key: 'providerOrganization',
                                label: translate.organization.organization,
                            },
                            {
                                key: 'activityTypes',
                                label: translate.common.activityType,
                            },
                            {
                                key: 'serviceProvider',
                                label: translate.practitioner.practitioner,
                            },
                        ]}
                        onExport={downloadExport}
                        showIterativeBatches={frontendSettings.exactIntegrationEnabled}
                    ></BatchesTable>
                </Card>

                <ConfirmationModal
                    handleNo={() => setShowConfirmationModal(false)}
                    handleYes={async () => {
                        setShowConfirmationModal(false);
                        setFormDisabled(true);
                        setExportDownloadFinishedWithErrors(false);
                        setExportDownloadId(undefined);
                        setExactClientEventId(undefined);

                        const result = await abortableFetch.fetchJson(getUrl(), 'GET');
                        setExportDownloadId(result.json.exportDownloadId);
                        if (frontendSettings.exactIntegrationEnabled) {
                            setExactClientEventId(result.json.exactClientEventId);
                        }
                    }}
                    isOpen={showConfirmationModal}
                    question={getConfirmationModalMessage()}
                    title={translate.common.notice}
                />
            </Form>
        </PageTemplate>
    );

    async function loadActivityTypeFilterAndEpisodeCategoryFilter() {
        const episodeCategoryFilterValues = await loadEpisodeCategoryFilter();

        setSearchParams({ ...searchParams, episodeCategoryFilterValues });
    }

    function handleChange<K extends keyof ISearchParams>(name: K, value: ISearchParams[K]) {
        setSearchParams({ ...searchParams, [name]: value });
    }

    function handleChangeSentBeforeDate(sentBeforeDate: string | null) {
        const bookYear = DateTime.from(sentBeforeDate || new Date()).year();
        setSearchParams({ ...searchParams, bookYear, sentBeforeDate });
    }

    function handleChangeActivityTypes(selectedActivityTypes: MultiValue<Option>) {
        const newValues = selectedActivityTypes.map((option) => option.value as PayoutType);

        handleChange('activityTypes', newValues);
        validateOne('activityTypes', newValues);
    }

    function validateOne<K extends keyof ISearchParams>(name: K, value: ISearchParams[K]) {
        setErrors(updateValidationErrorsForOne(paymentExportSchema, { ...searchParams, [name]: value }, errors, name));
    }

    async function loadServices() {
        let url = '/api/services?selectionList=true';

        if (searchParams.episodeCategoryId) {
            url += `&episodeCategoryId=${searchParams.episodeCategoryId}`;
        }

        const result = (await abortableFetch.fetchJson(url, 'GET')) as IFetchResult<{ items: IService[] }>;

        if (result.json?.items) {
            const optionsMap = result.json.items.reduce((resultMap, service) => {
                const episodeCategoryName = service.episodeCategory.name;
                const newOption: ISelectOption<string> = { text: service.name, value: service.id };

                if (resultMap.has(episodeCategoryName)) {
                    const currentOptions = resultMap.get(episodeCategoryName) || [];

                    resultMap.set(episodeCategoryName, [...currentOptions, newOption]);
                } else {
                    resultMap.set(episodeCategoryName, [newOption]);
                }

                return resultMap;
            }, new Map<string, ISelectOption<string>[]>());

            const options: ServiceOptions = [...optionsMap.entries()].map(([group, items]) => ({
                label: group,
                options: items,
            }));

            setServiceOptions(options);
        }
    }

    async function loadEpisodeCategoryFilter() {
        const result = await abortableFetch.fetchJson('/api/payments/payoutRecordEpisodeCategoryFilters', 'GET');
        if (result.json.items) {
            let episodeCategoryFilterValues = result.json.items.map((item: { id: string; name: string }) => {
                return {
                    text: translate.episodeCategoryEnum[item.name as keyof typeof getSelectedLanguage],
                    value: item.id,
                };
            });

            episodeCategoryFilterValues = episodeCategoryFilterValues.sort((a: IServiceOption, b: IServiceOption) => {
                if (a.text > b.text) {
                    return 1;
                } else if (a.text < b.text) {
                    return -1;
                } else {
                    return 0;
                }
            });

            return episodeCategoryFilterValues;
        }

        return [];
    }

    function getUrl(batchId?: string) {
        let url = '/api/payments/export?';

        if (batchId) {
            url += '&batchId=' + batchId;
        } else {
            url = addFiltersToUrl(url);
        }

        return url;
    }

    function getCountUrl() {
        return addFiltersToUrl('/api/payments/exportCount?');
    }

    function addFiltersToUrl(url: string) {
        url += `&isPreview=${searchParams.isPreview}`;
        if (searchParams.sentBeforeDate) {
            url += '&sentBeforeDate=' + searchParams.sentBeforeDate;
        }

        switch (searchParams.serviceChoice) {
            case 'specific':
                url += '&serviceId=' + searchParams.serviceId;
                break;
            case 'all':
                break;
            default:
                never(searchParams.serviceChoice);
        }

        if (searchParams.providerGroup) {
            url += '&providerGroup=' + searchParams.providerGroup;
        }

        if (searchParams.providerOrganization) {
            url += '&providerOrganization=' + searchParams.providerOrganization.value;
        }

        if (searchParams.episodeCategoryId) {
            url += '&episodeCategoryId=' + searchParams.episodeCategoryId;
        }

        if (searchParams.activityTypes) {
            url += searchParams.activityTypes.map((activityType) => `&activityTypes[]=${activityType}`).join('');
        }

        if (searchParams.serviceProvider) {
            const splitServiceProviderValue = searchParams.serviceProvider.value.split('#');
            url += '&providerPractitioner=' + splitServiceProviderValue[0];
            url += '&providerPractitionerOrganization=' + splitServiceProviderValue[1];
        }

        if (searchParams.expectedPayoutDate) {
            url += '&expectedPayoutDate=' + searchParams.expectedPayoutDate;
        }

        if (searchParams.bookYear) {
            url += '&bookYear=' + searchParams.bookYear;
        }

        if (searchParams.startDate) {
            url += '&startDate=' + searchParams.startDate;
        }

        if (searchParams.endDate) {
            url += '&endDate=' + searchParams.endDate;
        }

        return url;
    }

    async function downloadExport(batchId?: string) {
        const { errors: validateErrors } = validate(
            paymentExportSchema,
            batchId ? { ...searchParams, batchId } : searchParams
        );
        setErrors(validateErrors);
        if (validateErrors.size > 0) {
            return;
        }

        if (batchId) {
            setShowConfirmationModal(false);
            setFormDisabled(true);
            const result = await abortableFetch.fetchJson(getUrl(batchId), 'GET');
            setExportDownloadId(result.json.exportDownloadId);
        } else {
            const result = await abortableFetch.fetchJson(getCountUrl(), 'GET');
            setShowConfirmationModal(true);
            setCount(result.json.count);
        }
    }

    function getConfirmationModalMessage() {
        if (!searchParams.sentBeforeDate) {
            return '';
        }

        let message = `Weet je zeker dat je ${count} uitbetalingen tot en met de ${DateTime.from(
            searchParams.sentBeforeDate
        ).format('DD-MM-YYYY')}`;

        if (searchParams.serviceId && serviceOptions) {
            const serviceName = serviceOptions
                .map(
                    (serviceOptionsGroup) =>
                        (serviceOptionsGroup as ServiceOptionsGroup).options.find(
                            (option) => option.value === searchParams.serviceId
                        )?.text
                )
                .at(0);

            message += `, voor ${serviceName}`;
        }

        if (searchParams.providerGroup) {
            message += `, voor ${searchParams.providerGroup}`;
        }

        if (searchParams.providerOrganization) {
            message += `, voor ${searchParams.providerOrganization.label}`;
        }

        if (searchParams.activityTypes.length > 0) {
            const activityTypes = arrayToCommaSeparatedString(
                searchParams.activityTypes.map(
                    (activityType) => translate.payoutType[activityType as keyof typeof getSelectedLanguage]
                )
            );

            message += `, voor ${activityTypes}`;
        }

        if (searchParams.serviceProvider) {
            message += `, voor ${searchParams.serviceProvider.label}`;
        }

        message += searchParams.isPreview ? ', wilt proefexporteren?' : ', wilt exporteren?';

        return message;
    }
}
